import { useEffect, useRef } from 'react';

import {
  hideBackButton,
  mountBackButton,
  mountMainButton,
  onBackButtonClick,
  onMainButtonClick,
  setMainButtonParams,
  showBackButton,
  unmountBackButton,
  unmountMainButton,
} from '@telegram-apps/sdk-react';

import { isTelegramConfigured } from './init';

export interface MainButtonOptions {
  text: string;
  enabled?: boolean;
  loading?: boolean;
}

/**
 * Декларативная нативная MainButton: показывает её с текстом и обработчиком,
 * снимает при размонтировании. Экран не занимается mount/unmount и отпиской
 * руками — `options: null` прячет кнопку.
 *
 * Вне Telegram (или без мока в dev) `mountMainButton` недоступен — хук тихо
 * ничего не делает, кнопки на экране нет, но приложение не падает.
 */
export function useMainButton(options: MainButtonOptions | null, onClick: () => void): void {
  const handlerRef = useRef(onClick);
  handlerRef.current = onClick;

  const text = options?.text;
  const enabled = options?.enabled ?? true;
  const loading = options?.loading ?? false;

  useEffect(() => {
    if (!options || !isTelegramConfigured() || !mountMainButton.isAvailable()) return;

    mountMainButton();
    setMainButtonParams({
      text: options.text,
      isVisible: true,
      isEnabled: enabled,
      isLoaderVisible: loading,
    });
    const off = onMainButtonClick(() => handlerRef.current());

    return () => {
      off();
      if (setMainButtonParams.isAvailable()) setMainButtonParams({ isVisible: false });
      unmountMainButton();
    };
    // handlerRef держит актуальный onClick — пересоздавать подписку из-за
    // него не нужно, только из-за того, что реально меняет вид кнопки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options === null, text, enabled, loading]);
}

/**
 * Декларативная нативная BackButton. `onClick === null` — кнопка не показывается.
 */
export function useBackButton(onClick: (() => void) | null): void {
  const handlerRef = useRef(onClick);
  handlerRef.current = onClick;

  useEffect(() => {
    if (!onClick || !isTelegramConfigured() || !mountBackButton.isAvailable()) return;

    mountBackButton();
    if (showBackButton.isAvailable()) showBackButton();
    const off = onBackButtonClick(() => handlerRef.current?.());

    return () => {
      off();
      if (hideBackButton.isAvailable()) hideBackButton();
      unmountBackButton();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClick !== null]);
}
